import * as React from 'react';
import { useState, useEffect, useRef } from 'react';
import ContentLayout from 'components/common/layouts/ContentLayout';
import {
  getKstUSFormatDateWithTime,
  getUtcDateDash,
  getUtcDateDashDifference,
  getUtcUSFormatDateWithTime,
} from 'modules/utils/time';
import { PaymentMethod, InvoicePaymentStatus } from 'types/plansales/billing';
import { addQueryString, getNewValueForQuery, getParsedQuery } from 'modules/utils/queryString';
import { useHistory } from 'react-router-dom';
import { SearchDateTerms } from 'types/common/searchfilter';
import { FIRST_PAGE_INDEX } from 'constants/common';
import { GroupPlanType } from 'types/plansales/plan';
import { GetInvoiceHistoryResponse } from 'types/plansales/invoice/invoiceHistory';
import InvoiceHistorySearchFilter from './InvoiceHistorySearchFilter';
import { fetchInvoices } from 'api/plansales/invoice';
import InvoiceHistoryList from './InvoiceHistoryList';
import {
  groupPlanMap,
  paymentInvoiceStatusLabel,
  paymentMethodLabel,
} from 'constants/plansales/billing';

const defaultInterval = [
  getUtcDateDashDifference(new Date(), 1, 'month'),
  getUtcDateDash(new Date()),
];
const initialFilterValue = {
  dateInterval: defaultInterval,
  dateShortCut: SearchDateTerms['1_MONTH'],
  planType: {
    [GroupPlanType.STANDARD]: true,
    [GroupPlanType.PREMIUM]: true,
    [GroupPlanType.ADVANCED]: true,
  },
  payStatus: {
    [InvoicePaymentStatus.Completed]: true,
    [InvoicePaymentStatus.Pending]: true,
    [InvoicePaymentStatus.Refunded]: true,
    [InvoicePaymentStatus.Uncollectible]: true,
  },
  paymentMethod: {
    [PaymentMethod.STRIPE]: true,
    [PaymentMethod.CREDITS]: true,
  },
  keyword: '',
  pageNumber: FIRST_PAGE_INDEX,
};

const InvoiceHistoryContainer = () => {
  const history = useHistory();
  const exportExcelRef = useRef<HTMLButtonElement>(null);

  const [selectedFilterValues, setSelectedFilterValues] = useState(initialFilterValue);
  const [searchResultData, setSearchResultData] = useState<GetInvoiceHistoryResponse | null>(null);
  const [isResultLoading, setIsResultLoading] = useState(false);
  const [excelData, setExcelData] = useState<any[]>([{ status: 'NO DATA' }]);
  const [isFetchingExcelData, setFetchingExcelData] = useState<boolean>(false);

  useEffect(() => {
    // 새로고침시 url query 읽어와서 세팅
    const queries = history.location.search;
    const parsedQuery = getParsedQuery(queries);
    const searchParams = {
      dateInterval: parsedQuery?.['date'] ?? initialFilterValue.dateInterval,
      planType: {
        [GroupPlanType.STANDARD]:
          parsedQuery?.['planType']?.includes(String(GroupPlanType.STANDARD)) ?? true,
        [GroupPlanType.PREMIUM]:
          parsedQuery?.['planType']?.includes(String(GroupPlanType.PREMIUM)) ?? true,
        [GroupPlanType.ADVANCED]:
          parsedQuery?.['planType']?.includes(String(GroupPlanType.ADVANCED)) ?? true,
      },
      payStatus: {
        [InvoicePaymentStatus.Pending]:
          parsedQuery?.['payStatus']?.includes(String(InvoicePaymentStatus.Pending)) ?? true,
        [InvoicePaymentStatus.Uncollectible]:
          parsedQuery?.['payStatus']?.includes(String(InvoicePaymentStatus.Uncollectible)) ?? true,
        [InvoicePaymentStatus.Completed]:
          parsedQuery?.['payStatus']?.includes(String(InvoicePaymentStatus.Completed)) ?? true,
        [InvoicePaymentStatus.Refunded]:
          parsedQuery?.['payStatus']?.includes(String(InvoicePaymentStatus.Refunded)) ?? true,
      },
      paymentMethod: {
        [PaymentMethod.STRIPE]:
          parsedQuery?.['paymentMethod']?.includes(String(PaymentMethod.STRIPE)) ?? true,
        [PaymentMethod.CREDITS]:
          parsedQuery?.['paymentMethod']?.includes(String(PaymentMethod.CREDITS)) ?? true,
      },
      dateShortCut: parsedQuery['shortcut']?.[0] ?? initialFilterValue.dateShortCut,
      keyword: parsedQuery['keyword']?.[0] ?? initialFilterValue.keyword,
      pageNo: parsedQuery['pageNo'] ? Number(parsedQuery['pageNo']) : FIRST_PAGE_INDEX,
    };
    // @ts-ignore
    setSelectedFilterValues(searchParams);
    fetchInvoices({
      startDate: searchParams.dateInterval[0],
      endDate: searchParams.dateInterval[1],
      keyword: searchParams.keyword,
      pageNo: searchParams.pageNo,
      paymentMethod: searchParams.paymentMethod,
      paymentStatus: searchParams.payStatus,
      planType: searchParams.planType,
    })
      .then(res => setSearchResultData(res.data))
      .catch(() => setSearchResultData(null));
  }, []);

  const resetSearchFilter = () => {
    addQueryString({ history, reset: true });
    setSelectedFilterValues(initialFilterValue);
  };

  const fetchSearchData = (page: number = FIRST_PAGE_INDEX) => {
    setIsResultLoading(true);
    setSearchResultData(null);
    addQueryString({
      history,
      objValue: {
        ...selectedFilterValues,
        payStatus: getNewValueForQuery(selectedFilterValues.payStatus),
        planType: getNewValueForQuery(selectedFilterValues.planType),
        paymentMethod: getNewValueForQuery(selectedFilterValues.paymentMethod),
        pageNo: page,
      },
    });
    fetchInvoices({
      startDate: selectedFilterValues.dateInterval[0],
      endDate: selectedFilterValues.dateInterval[1],
      keyword: selectedFilterValues.keyword,
      pageNo: page,
      paymentMethod: selectedFilterValues.paymentMethod,
      paymentStatus: selectedFilterValues.payStatus,
      planType: selectedFilterValues.planType,
    })
      .then(res => setSearchResultData(res.data))
      .catch(() => setSearchResultData(null));
    setIsResultLoading(false);
  };

  const onClickExport = async () => {
    if (!exportExcelRef.current) return;
    if (isFetchingExcelData) return;
    setFetchingExcelData(true);
    await fetchInvoices({
      startDate: selectedFilterValues.dateInterval[0],
      endDate: selectedFilterValues.dateInterval[1],
      keyword: selectedFilterValues.keyword,
      pageNo: 1,
      pageSize: 999999,
      paymentMethod: selectedFilterValues.paymentMethod,
      paymentStatus: selectedFilterValues.payStatus,
      planType: selectedFilterValues.planType,
    })
      .then(res => setExcelData(makeExcelData(res.data)))
      .finally(() => setFetchingExcelData(false));
    exportExcelRef.current.click();
  };

  return (
    <ContentLayout pageTitle={'Invoice'}>
      <InvoiceHistorySearchFilter
        resetSearchFilter={resetSearchFilter}
        fetchSearchData={fetchSearchData}
        selectedFilterValues={selectedFilterValues}
        setSelectedFilterValues={setSelectedFilterValues}
      />
      {searchResultData && (
        <InvoiceHistoryList
          ref={exportExcelRef}
          dateInterval={selectedFilterValues.dateInterval}
          searchResultData={searchResultData}
          onClickExport={onClickExport}
          searchResultDataLoading={isResultLoading}
          fetchResultData={fetchSearchData}
          excelData={excelData}
          isFetchingExcelData={isFetchingExcelData}
          pageNo={selectedFilterValues['pageNumber'] ?? FIRST_PAGE_INDEX}
        />
      )}
    </ContentLayout>
  );
};

const makeExcelData = (data: GetInvoiceHistoryResponse) => {
  const items = data.items;
  if (!items) return [];
  const result = items.map(item => {
    return {
      ['Invoice No.']: item.invoiceNo,
      ['Group ID']: item.groupId,
      ['Group Name']: item.groupName,
      ['Admin Email']: item.adminEmail,
      ['Plan Type']: groupPlanMap[item.planType],
      ['Payment Method']: paymentMethodLabel[item.paymentMethod],
      ['Billed Amount (Including tax)']: (item.totalAmount + item.taxAmount).toFixed(2),
      ['Payment by Credit']: item.creditAmount.toFixed(2),
      ['Payment by Card (Including tax)']: (item.cardAmount + item.taxAmount).toFixed(2),
      ['Pay Status']: paymentInvoiceStatusLabel[item.paymentStatus as InvoicePaymentStatus],
      ['Payment Date (UTC)']:
        item.paymentStatus !== InvoicePaymentStatus.Completed &&
        item.paymentStatus !== InvoicePaymentStatus.Refunded
          ? ''
          : `${getUtcUSFormatDateWithTime(item.paymentSuccessAt, true)}`,
      ['Payment Date(KST)']:
        item.paymentStatus !== InvoicePaymentStatus.Completed &&
        item.paymentStatus !== InvoicePaymentStatus.Refunded
          ? ''
          : `${getKstUSFormatDateWithTime(item.paymentSuccessAt, true)}`,
      ['PaymentTxId']: item.paymentInfo.paymentTaxId ?? '',
      ['Avalara Doc Code']: item.paymentInfo.avalaraDocCode ?? '',
    };
  });
  return result;
};

export default InvoiceHistoryContainer;
